import React from "react";
import PropTypes from "prop-types";
//Material imports
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import MUITabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import makeStyles from "@mui/styles/makeStyles";
// Sections
import BrandLearningHeader from "../../sections/brand-learning-header/BrandLearningHeader";
import BrandLearningCta from "../../sections/brand-learning-cta/BrandLearningCta";
import PropAndRewards from "../../sections/prop-rewards/PropAndRewards";
import RichText from "../../sections/rich-text/RichText";
import TemplateRenderer from "../../components/template-renderer/TemplateRenderer";
import StepsContent from "../../sections/steps-content/StepsContent";
import Testimonials from "../../sections/testimonials/Testimonials";
import ExampleQuestions from "../../sections/example-questions/ExampleQuestions";
import BrandLearningTabHeader from "../../sections/brand-learning-tab-header/BrandLearningTabHeader";
import TextWithMeadia from "../../sections/text-with-media/TextWithMeadia";
import TestimonialsWithMedia from "../../sections/testimonials-with-media/TestimonialsWithMedia";
import CanCannotAnswer from "../../sections/can-cannot-answer/CanCannotAnswer";
import { useGroupSignUpStyles } from "../group-sign-up/GroupSignUpTemplate";
import TableContent from "../../sections/table-content/TableContent";
const TAB_SECTIONS = {
  header: BrandLearningTabHeader,
  "text-with-media": TextWithMeadia,
  "can-cannot-answer": CanCannotAnswer,
  "prop-rewards": PropAndRewards,
  steps: StepsContent,
  testimonials: Testimonials,
  "example-questions": ExampleQuestions,
  "rich-text": RichText,
  "testimonials-with-media": TestimonialsWithMedia,
  table: TableContent
};
const useStyles = makeStyles(theme => ({
  ...useGroupSignUpStyles(theme),
  containerPadding: {
    padding: 65
  },
  tabContentContainer: {
    // backgroundColor: "#fff",
    borderRadius: 12,
    scrollMarginTop: "3rem",
    padding: "32px 0px 0px"
  },
  leftSpacing: {
    paddingLeft: 0
  },
  richTextStyle: {
    paddingBottom: 32
  },
  headerContainerStyle: {
    marginTop: 0
  },
  subHeaderContainer: {
    paddingLeft: 0
  },
  bottomSpacing: {
    paddingBottom: 32
  },
  tab: {
    padding: 16,
    marginLeft: 4,
    marginRight: 4,
    minWidth: 70
  },
  tabs: {
    position: "sticky",
    top: 0,
    background: theme.palette.background.default
  },
  button: {
    textTransform: "none",
    padding: 16,
    marginLeft: 4,
    marginRight: 4,
    minWidth: 70,
    "&.Mui-selected": {
      color: theme.palette.text.primary
    }
  },
  scrollButtons: {
    color: theme.palette.secondary.main
  }
}));
const BrandLearningTemplate = ({
  content,
  brandLearningCtaClick,
  handleChangeSelectedTab,
  isFullScreen = false
}) => {
  var _content$headerAndCta, _content$headerAndCta2, _content$tabSections6;
  const [selectedTabIndex, setSelectedTabIndex] = React.useState(0);
  const [hasUserChangedTabs, setHasUserChangedTabs] = React.useState(false);
  const classes = useStyles();
  React.useEffect(() => {
    var _content$tabSections;
    content === null || content === void 0 ? void 0 : (_content$tabSections = content.tabSections) === null || _content$tabSections === void 0 ? void 0 : _content$tabSections.find(item => {
      if (Reflect.getOwnPropertyDescriptor(item, "index")) {
        setSelectedTabIndex(item.index);
      }
    });
  }, []);
  React.useEffect(() => {
    //only scroll to top if the user has changed the tab
    if (hasUserChangedTabs) {
      const height = document.querySelector("#tabContainer").offsetTop;
      if (height >= 358) {
        scrollToTop();
      }
    }
  }, [selectedTabIndex]);
  const renderTabContent = React.useMemo(() => {
    var _content$tabSections2;
    if (((_content$tabSections2 = content.tabSections) === null || _content$tabSections2 === void 0 ? void 0 : _content$tabSections2.length) > 0) {
      return /*#__PURE__*/React.createElement(Grid, {
        className: classes.tabContentContainer,
        id: "scrollDiv",
        item: true,
        xs: 11,
        md: 11,
        lg: 11,
        sm: 11
      }, /*#__PURE__*/React.createElement(TemplateRenderer, {
        content: content.tabSections[selectedTabIndex],
        sectionComponentMapping: TAB_SECTIONS,
        leftSpaceContainer: classes.leftSpacing,
        extraRichTextStyle: classes.richTextStyle,
        extraHeaderStyle: classes.headerContainerStyle,
        bottomSpaceContainer: classes.bottomSpacing,
        subHeaderContainer: classes.subHeaderContainer,
        useAltDesign: true
      }));
    }
    return null;
  }, [selectedTabIndex, content.tabSections]);
  const scrollToTop = () => {
    const section = document.querySelector("#scrollDiv");
    section.scrollIntoView({
      behavior: "smooth",
      block: "start"
    });
  };
  const handleTabChange = (event, newValue) => {
    setHasUserChangedTabs(true);
    setSelectedTabIndex(newValue);
    if (typeof handleChangeSelectedTab === "function") {
      const selectedTabSection = content === null || content === void 0 ? void 0 : content.tabSections[newValue];
      handleChangeSelectedTab(selectedTabSection);
    }
  };
  const brandLearningCtaBackClick = event => {
    var _content$tabSections3, _content$tabSections4;
    handleTabChange(event, (selectedTabIndex + ((_content$tabSections3 = content.tabSections) === null || _content$tabSections3 === void 0 ? void 0 : _content$tabSections3.length) - 1) % ((_content$tabSections4 = content.tabSections) === null || _content$tabSections4 === void 0 ? void 0 : _content$tabSections4.length));
  };
  const brandLearningCtaNextClick = event => {
    var _content$tabSections5;
    handleTabChange(event, (selectedTabIndex + 1) % ((_content$tabSections5 = content.tabSections) === null || _content$tabSections5 === void 0 ? void 0 : _content$tabSections5.length));
  };
  if (!content) {
    return null;
  }
  const brandLearningHeaderContent = content === null || content === void 0 ? void 0 : (_content$headerAndCta = content.headerAndCtaSections) === null || _content$headerAndCta === void 0 ? void 0 : _content$headerAndCta.find(section => (section === null || section === void 0 ? void 0 : section.sectionName) === "brand-learning-header");
  const brandLearningCtaContent = content === null || content === void 0 ? void 0 : (_content$headerAndCta2 = content.headerAndCtaSections) === null || _content$headerAndCta2 === void 0 ? void 0 : _content$headerAndCta2.find(section => section.sectionName === "brand-learning-cta");
  return /*#__PURE__*/React.createElement(Grid, {
    className: `${isFullScreen ? `${classes.container} ${classes.containerPadding}` : ""}`,
    xs: 12,
    md: 12,
    lg: 12,
    sm: 12,
    display: "flex",
    flexDirection: "column",
    alignItems: "center"
  }, brandLearningHeaderContent && /*#__PURE__*/React.createElement(BrandLearningHeader, {
    content: brandLearningHeaderContent
  }), ((_content$tabSections6 = content.tabSections) === null || _content$tabSections6 === void 0 ? void 0 : _content$tabSections6.length) > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Grid, {
    id: "tabContainer",
    xs: 11,
    md: 11,
    lg: 11,
    sm: 11,
    item: true,
    className: classes.tabs,
    display: "flex",
    justifyContent: "flex-start",
    width: "100%"
  }, /*#__PURE__*/React.createElement(MUITabs, {
    value: selectedTabIndex,
    onChange: handleTabChange,
    variant: "scrollable",
    scrollButtons: "auto",
    textColor: "secondary",
    classes: {
      scrollButtons: classes.scrollButtons
    }
  }, content.tabSections.map((section, index) => {
    return /*#__PURE__*/React.createElement(Tab, {
      key: index,
      id: section.tabTitle,
      label: section.tabTitle,
      classes: {
        root: classes.button
      },
      value: index
    });
  }))), renderTabContent), brandLearningCtaContent && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 11.5,
    md: 11.5,
    lg: 11.5,
    sm: 11.5,
    width: "100%"
  }, /*#__PURE__*/React.createElement(BrandLearningCta, {
    content: brandLearningCtaContent,
    selectedTabIndex: selectedTabIndex,
    tabContent: content,
    brandLearningCtaClick: brandLearningCtaClick,
    brandLearningCtaBackClick: brandLearningCtaBackClick,
    brandLearningCtaNextClick: brandLearningCtaNextClick
  })));
};
BrandLearningTemplate.propTypes = {
  content: PropTypes.object.isRequired,
  brandLearningCtaClick: PropTypes.func,
  handleChangeSelectedTab: PropTypes.func,
  isFullScreen: PropTypes.bool
};
export default BrandLearningTemplate;